import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const DATA_POINTS_LIMIT = 100;
const AGGREGATION_INTERVAL = 1; // minutes
const REQUEST_TIMEOUT = 30000;

const VibrationChart = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const deviceId1 = queryParams.get('device1');
    const deviceId2 = queryParams.get('device2');

    const [vibrationData1, setVibrationData1] = useState({ data: [], originalCount: 0 });
    const [vibrationData2, setVibrationData2] = useState({ data: [], originalCount: 0 });
    const [loading1, setLoading1] = useState(true);
    const [loading2, setLoading2] = useState(true);
    const [error1, setError1] = useState(null);
    const [error2, setError2] = useState(null);
    const [devices, setDevices] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(true);
    const [deviceError, setDeviceError] = useState(null);
    const [paramType, setParamType] = useState('acceleration');

    const abortControllers = useRef([]);

    const fetchVibrationData = useCallback(async (deviceId, setData, setLoading, setError) => {
        if (!deviceId) {
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        abortControllers.current.push(controller);

        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`/api/Meters/vibration_history/${deviceId}`, {
                params: {
                    limit: DATA_POINTS_LIMIT,
                    intervalMinutes: AGGREGATION_INTERVAL,
                },
                signal: controller.signal,
                timeout: REQUEST_TIMEOUT
            });
            setData({
                data: response.data.data || [],
                originalCount: response.data.totalCount || 0
            });
        } catch (err) {
            if (!axios.isCancel(err)) {
                setError(`Failed to load vibration data for device ${deviceId}`);
                console.error('Vibration data error:', err);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDeviceData = useCallback(async () => {
        const controller = new AbortController();
        abortControllers.current.push(controller);
        try {
            setLoadingDevices(true);
            setDeviceError(null);
            const response = await axios.get('/api/meters', {
                params: { fields: 'id,deviceId,deviceName,label' },
                signal: controller.signal,
                timeout: REQUEST_TIMEOUT
            });
            const deviceList = response.data.map(item => ({
                id: item.id || item.deviceId,
                deviceId: item.deviceId,
                deviceName: item.deviceName || `Device ${item.deviceId}`,
                label: item.label || ''
            }));
            setDevices(deviceList);
        } catch (err) {
            if (!axios.isCancel(err)) {
                setDeviceError('Failed to load device list');
                console.error('Device data error:', err);
            }
        } finally {
            setLoadingDevices(false);
        }
    }, []);

    useEffect(() => {
        return () => {
            abortControllers.current.forEach(controller => controller.abort());
            abortControllers.current = [];
        };
    }, []);

    useEffect(() => {
        fetchDeviceData();
    }, [fetchDeviceData]);

    useEffect(() => {
        if (deviceId1) fetchVibrationData(deviceId1, setVibrationData1, setLoading1, setError1);
        if (deviceId2) fetchVibrationData(deviceId2, setVibrationData2, setLoading2, setError2);
    }, [deviceId1, deviceId2, fetchVibrationData]);

    // Helper to convert null/undefined to null (no zero default)
    const safeValue = (val, divisor = 1) => (val != null ? val / divisor : null);

    // For angle: raw stored as int *200, display = (raw/200)*0.005
    const angleDisplay = (raw) => (raw != null ? (raw / 200) * 0.005 : null);

    const prepareChartData = useCallback((vibrationData) => {
        if (!vibrationData || vibrationData.length === 0) {
            return { labels: [], datasets: [], yAxisOptions: {} };
        }

        const labels = vibrationData.map(item => format(parseISO(item.createdAt), 'HH:mm'));

        let datasets = [];
        let yAxisTitle = '';
        let suggestedMin = 0;
        let suggestedMax = 10;
        let stepSize = undefined;

        switch (paramType) {
            case 'acceleration':
                datasets = [
                    { label: 'Acc X (g)', data: vibrationData.map(item => safeValue(item.accX, 1000)), borderColor: 'rgb(255,99,132)', backgroundColor: 'rgba(255,99,132,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false },
                    { label: 'Acc Y (g)', data: vibrationData.map(item => safeValue(item.accY, 1000)), borderColor: 'rgb(54,162,235)', backgroundColor: 'rgba(54,162,235,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false },
                    { label: 'Acc Z (g)', data: vibrationData.map(item => safeValue(item.accZ, 1000)), borderColor: 'rgb(75,192,192)', backgroundColor: 'rgba(75,192,192,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false }
                ];
                yAxisTitle = 'Acceleration (g)';
                suggestedMin = -2;
                suggestedMax = 20;
                stepSize = 2;
                break;

            case 'velocity':
                datasets = [
                    { label: 'Vel X (m/s²)', data: vibrationData.map(item => safeValue(item.velX, 1000)), borderColor: 'rgb(255,159,64)', backgroundColor: 'rgba(255,159,64,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false },
                    { label: 'Vel Y (m/s²)', data: vibrationData.map(item => safeValue(item.velY, 1000)), borderColor: 'rgb(153,102,255)', backgroundColor: 'rgba(153,102,255,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false },
                    { label: 'Vel Z (m/s²)', data: vibrationData.map(item => safeValue(item.velZ, 1000)), borderColor: 'rgb(255,206,86)', backgroundColor: 'rgba(255,206,86,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false }
                ];
                yAxisTitle = 'Velocity (m/s²)';
                suggestedMin = -2;
                suggestedMax = 20;
                stepSize = 2;
                break;

            case 'angle':
                datasets = [
                    { label: 'Angle X (°)', data: vibrationData.map(item => angleDisplay(item.angleX)), borderColor: 'rgb(201,203,207)', backgroundColor: 'rgba(201,203,207,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false },
                    { label: 'Angle Y (°)', data: vibrationData.map(item => angleDisplay(item.angleY)), borderColor: 'rgb(255,99,71)', backgroundColor: 'rgba(255,99,71,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false },
                    { label: 'Angle Z (°)', data: vibrationData.map(item => angleDisplay(item.angleZ)), borderColor: 'rgb(60,179,113)', backgroundColor: 'rgba(60,179,113,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false }
                ];
                yAxisTitle = 'Angle (degrees)';
                suggestedMin = -180;
                suggestedMax = 180;
                stepSize = 30;
                break;

            case 'temperature':
                datasets = [
                    { label: 'Temperature (°C)', data: vibrationData.map(item => safeValue(item.temperature, 10)), borderColor: 'rgb(220,20,60)', backgroundColor: 'rgba(220,20,60,0.5)', tension: 0.3, pointRadius: 2, spanGaps: false, fill: false }
                ];
                yAxisTitle = 'Temperature (°C)';
                suggestedMin = -10;
                suggestedMax = 60;
                stepSize = 5;
                break;

            default:
                break;
        }

        // Auto-adjust min/max based on actual data (if present)
        let allValues = [];
        datasets.forEach(ds => {
            ds.data.forEach(v => { if (v != null) allValues.push(v); });
        });
        if (allValues.length > 0) {
            const minVal = Math.min(...allValues);
            const maxVal = Math.max(...allValues);
            const padding = (maxVal - minVal) * 0.1 || 1;
            suggestedMin = Math.floor(minVal - padding);
            suggestedMax = Math.ceil(maxVal + padding);
            // Avoid stepSize too small or too large
            const range = suggestedMax - suggestedMin;
            if (range > 0) stepSize = Math.ceil(range / 6);
        }

        return { labels, datasets, yAxisTitle, suggestedMin, suggestedMax, stepSize };
    }, [paramType]);

    const chartOptions = useCallback((deviceId, deviceName, yAxisTitle, suggestedMin, suggestedMax, stepSize) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: `${deviceName} - ${paramType.charAt(0).toUpperCase() + paramType.slice(1)}`, font: { size: 16 } },
            tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(4)}` } },
            legend: { position: 'top' }
        },
        scales: {
            y: {
                title: { display: true, text: yAxisTitle },
                min: suggestedMin,
                max: suggestedMax,
                ticks: { stepSize: stepSize, precision: 2, callback: (value) => value.toFixed(2) },
                grid: { color: 'rgba(0,0,0,0.1)', drawBorder: true, lineWidth: 1 }
            },
            x: {
                title: { display: true, text: 'Time (Last 24 Hours)' },
                ticks: { maxTicksLimit: 12, autoSkip: true },
                grid: { display: false }
            }
        },
        elements: { point: { radius: 2, hoverRadius: 4 }, line: { borderWidth: 1.5 } }
    }), [paramType]);

    const getDeviceById = useCallback((deviceId) => {
        if (!deviceId) return null;
        if (loadingDevices) return { deviceName: 'Loading...', id: deviceId };
        if (deviceError) return { deviceName: `Device ${deviceId} (Error)`, id: deviceId };
        const device = devices.find(d => d.id == deviceId || d.deviceId == deviceId);
        return device || { deviceName: `Device ${deviceId}`, id: deviceId };
    }, [devices, loadingDevices, deviceError]);

    const device1 = getDeviceById(deviceId1);
    const device2 = getDeviceById(deviceId2);

    const renderBatteryInfo = (data) => {
        if (!data || data.length === 0) return null;
        const latest = data[data.length - 1];
        if (!latest.battery) return null;
        const batteryVoltage = (latest.battery * 0.1).toFixed(1);
        return <span className="badge bg-info ms-2">Battery: {batteryVoltage}V</span>;
    };

    const renderChart = (dataObj, loading, error, device, fetchFn) => {
        const { data, originalCount } = dataObj;
        if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /><p>Loading vibration data...</p></div>;
        if (error) return <div className="alert alert-danger text-center">{error}<button className="btn btn-sm btn-outline-secondary ms-3" onClick={fetchFn}>Retry</button></div>;
        if (!data.length) return <div className="alert alert-warning text-center">No data available</div>;
        const { labels, datasets, yAxisTitle, suggestedMin, suggestedMax, stepSize } = prepareChartData(data);
        return <Line data={{ labels, datasets }} options={chartOptions(device?.id, device?.deviceName, yAxisTitle, suggestedMin, suggestedMax, stepSize)} />;
    };

    return (
        <div className="container mt-4">
            <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                    <h2 className="mb-0">Vibration Analysis Comparison</h2>
                    {deviceError && <div className="alert alert-danger mt-2">{deviceError}<button className="btn btn-sm btn-light ms-3" onClick={fetchDeviceData}>Retry</button></div>}
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body">
                            <label className="form-label fw-bold">Parameter Type:</label>
                            <select value={paramType} onChange={(e) => setParamType(e.target.value)} className="form-select">
                                <option value="acceleration">Acceleration (g)</option>
                                <option value="velocity">Velocity (m/s²)</option>
                                <option value="angle">Angle (degrees)</option>
                                <option value="temperature">Temperature (°C)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <div><h3 className="mb-0">{device1?.deviceName || 'Device 1'}</h3>{device1?.label && <span className="badge bg-secondary ms-2">{device1.label}</span>}{renderBatteryInfo(vibrationData1.data)}</div>
                        </div>
                        <div className="card-body" style={{ height: '400px' }}>
                            {renderChart(vibrationData1, loading1, error1, device1, () => fetchVibrationData(deviceId1, setVibrationData1, setLoading1, setError1))}
                        </div>
                        <div className="card-footer text-muted">Showing {vibrationData1.data.length} of {vibrationData1.originalCount} readings{vibrationData1.data.length < vibrationData1.originalCount && ' (sampled)'}</div>
                    </div>
                </div>
                {deviceId2 && (
                    <div className="col-md-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div><h3 className="mb-0">{device2?.deviceName || 'Device 2'}</h3>{device2?.label && <span className="badge bg-secondary ms-2">{device2.label}</span>}{renderBatteryInfo(vibrationData2.data)}</div>
                            </div>
                            <div className="card-body" style={{ height: '400px' }}>
                                {renderChart(vibrationData2, loading2, error2, device2, () => fetchVibrationData(deviceId2, setVibrationData2, setLoading2, setError2))}
                            </div>
                            <div className="card-footer text-muted">Showing {vibrationData2.data.length} of {vibrationData2.originalCount} readings{vibrationData2.data.length < vibrationData2.originalCount && ' (sampled)'}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VibrationChart;